// areas.js — 歩き回れる場所（タイルマップ）の定義
//
// ホール・店の外・自宅の 3 か所。どこも同じ仕組みで動く：
//   map      1 文字 1 タイル
//   solid    通れない記号
//   palette  床と壁の色（場所ごとの空気を作る）
//   scenery  記号ごとの描きかた（ctx, x, y, frame）
//   spots    調べると何かが開く記号
//   doors    別の場所へ繋がる記号。調べると移動する
//
// 表に出る名前や案内文は言語ごとに変わるので js/locales/ にある。ここが
// 持つのは鍵（key）だけで、area.<id>.title / spot.<key>.name のように引く。
//
// 台や NPC や自作台の枠は場所ごとに違う記号を割り当てる。記号が二役を持つと
// 片方が黙ってもう片方に化けるので、world.js の起動時に重複を見ている。

export const TILE = 26;

const TAU = Math.PI * 2;

// ---------------------------------------------------------------- 共通の描画

function rect(ctx, color, x, y, w, h) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

function drawPlant(ctx, x, y) {
  const cx = x + TILE / 2, by = y + TILE;
  rect(ctx, '#4a1a20', cx - 6, by - 9, 12, 8);
  ctx.fillStyle = '#2f7d5b';
  for (const [dx, dy, r] of [[0, -16, 7], [-6, -12, 5], [6, -12, 5]]) {
    ctx.beginPath();
    ctx.arc(cx + dx, by + dy, r, 0, TAU);
    ctx.fill();
  }
}

function drawVending(ctx, x, y) {
  rect(ctx, '#2a0c14', x + 2, y - 8, TILE - 4, TILE + 8);
  rect(ctx, '#f0435f', x + 4, y - 6, TILE - 8, 9);
  rect(ctx, '#0a0f20', x + 4, y + 5, TILE - 8, 12);
  rect(ctx, 'rgba(255,255,255,0.25)', x + 5, y + 7, TILE - 10, 3);
}

function drawCounter(ctx, x, y) {
  rect(ctx, '#5c1424', x, y, TILE, TILE);
  rect(ctx, '#7d1c2e', x, y + TILE - 7, TILE, 7);
  rect(ctx, 'rgba(255,209,102,0.85)', x, y + TILE - 8, TILE, 1.5);
}

function drawIsland(ctx, x, y) {
  rect(ctx, '#3a0e1a', x, y, TILE, TILE);
  rect(ctx, 'rgba(255,150,120,0.2)', x, y + 3, TILE, 2);
}

// ---------------------------------------------------------------- ホール

const HALL_MAP = [
  '###############',
  '#.............#',
  '#.=====K=====.#',
  '#...a.........#',
  '#.............#',
  '#...IIIIII....#',
  '#...012345....#',
  '#.............#',
  '#...IIIIII....#',
  '#...jklmno....#',
  '#..P.......P..#',
  '#.b...........#',
  '#.........c...#',
  '#.............#',
  '#..V.......V..#',
  '#.............#',
  '#....d........#',
  '#.....EEE.....#',
  '#.............#',
  '######XXX######',
];

// ---------------------------------------------------------------- 店の外

// パチンコ屋の隣に、なぜか小さな窓口が建っている。日本のホールの周りに
// 実在するあれ。看板には「景品交換所」としか書いていない。
const STREET_MAP = [
  '###############',
  '#BBBBBBB#KKK..#',
  '#BBBDBBB#KwK..#',
  '#.............#',
  '#.............#',
  '#....T...T....#',
  '#.............#',
  '#.............#',
  '#..V.......e..#',
  '#.............#',
  '#....T...T....#',
  '#.............#',
  '#HHH..........#',
  '#HhH..........#',
  '#.............#',
  '###############',
];

function drawFacade(ctx, x, y, frame) {
  rect(ctx, '#2a0a16', x, y, TILE, TILE);
  const pulse = 0.5 + 0.5 * Math.sin(frame * 0.06 + x * 0.1);
  ctx.globalAlpha = 0.35 + pulse * 0.4;
  rect(ctx, '#ff3d5a', x + 2, y + 4, TILE - 4, 3);
  ctx.globalAlpha = 1;
  rect(ctx, 'rgba(255,209,102,0.25)', x + 3, y + 12, TILE - 6, 8);
}

function drawHut(ctx, x, y) {
  rect(ctx, '#241c2c', x, y, TILE, TILE);
  rect(ctx, '#3b2f46', x, y, TILE, 4);
  rect(ctx, 'rgba(200,220,255,0.10)', x + 3, y + 9, TILE - 6, 7);
}

function drawHouse(ctx, x, y) {
  rect(ctx, '#2b2340', x, y, TILE, TILE);
  rect(ctx, '#413566', x, y, TILE, 5);
  rect(ctx, 'rgba(255,220,150,0.35)', x + 6, y + 11, TILE - 12, 7);
}

function drawTree(ctx, x, y) {
  const cx = x + TILE / 2, by = y + TILE;
  rect(ctx, '#3a2a1c', cx - 3, by - 12, 6, 11);
  ctx.fillStyle = '#2c6b4a';
  for (const [dx, dy, r] of [[0, -20, 9], [-7, -14, 6], [7, -14, 6]]) {
    ctx.beginPath();
    ctx.arc(cx + dx, by + dy, r, 0, TAU);
    ctx.fill();
  }
}

// ---------------------------------------------------------------- 自宅

const HOME_MAP = [
  '#############',
  '#SSSSS..BB..#',
  '#...........#',
  '#...........#',
  '#..t........#',
  '#...........#',
  '#.......P...#',
  '#...........#',
  '#...........#',
  '#####XX######',
];

function drawBed(ctx, x, y) {
  rect(ctx, '#3a2b56', x, y + 2, TILE, TILE - 4);
  rect(ctx, '#e8e2ff', x + 2, y + 4, TILE - 4, 9);
  rect(ctx, '#7c6bb0', x + 2, y + 14, TILE - 4, 8);
}

function drawTable(ctx, x, y) {
  rect(ctx, '#5a4327', x + 2, y + 6, TILE - 4, 13);
  rect(ctx, '#7a5d38', x + 2, y + 6, TILE - 4, 4);
}

// 棚は world.js 側でコレクションを載せて描くので、ここでは板だけ
function drawShelf(ctx, x, y) {
  rect(ctx, '#3c2f22', x, y, TILE, TILE);
  rect(ctx, '#584635', x, y + TILE - 6, TILE, 6);
  rect(ctx, 'rgba(255,220,170,0.18)', x, y + TILE - 7, TILE, 1.5);
}

// ---------------------------------------------------------------- 定義

export const AREAS = {
  hall: {
    id: 'hall',
    map: HALL_MAP,
    solid: '#=IPVKXjklmnoabcd012345',
    machines: '012345',            // MACHINES の添字
    customs: 'jklmno',             // 自作台の枠
    npcs: 'abcd',                  // NPCS の並び順
    palette: { floor: ['#33101c', '#2b0d18'], wall: '#1a0710', edge: 'rgba(255,80,90,0.65)' },
    ambient: 'rgba(255,80,80,0.10)',
    background:
      'radial-gradient(120% 55% at 50% 0%, rgba(220, 40, 60, 0.34), transparent 68%),'
      + 'linear-gradient(180deg, #1b0610, #0b0308 62%)',
    scenery: {
      E: (ctx, x, y) => {
        rect(ctx, '#4a1220', x, y, TILE, TILE);
        ctx.strokeStyle = 'rgba(255,190,110,0.45)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 2.5, y + 2.5, TILE - 5, TILE - 5);
      },
      '=': drawCounter,
      I: drawIsland,
      P: drawPlant,
      V: drawVending,
    },
    // 景品カウンター。店員がカウンターの向こうに立っている
    spots: {
      K: { kind: 'prizes', key: 'counter', color: '#ffd166', clerk: { body: '#e05a6a' } },
    },
    doors: { X: { to: 'street', spawn: [4, 3], key: 'out', color: '#8ef2ff' } },
    spawn: [7, 18],
    // 案内係。タイルではなく動ける存在なので、ここには持ち場だけ書く
    guide: { home: [9, 18], body: '#3fae7a', color: '#7fdcaa' },
  },

  street: {
    id: 'street',
    map: STREET_MAP,
    solid: '#BKHTVDwhe',
    npcs: '',
    palette: { floor: ['#171a26', '#141722'], wall: '#0a0c14', edge: 'rgba(140,170,255,0.35)' },
    ambient: 'rgba(60,90,180,0.10)',
    background:
      'radial-gradient(120% 55% at 50% 0%, rgba(40, 60, 140, 0.34), transparent 68%),'
      + 'linear-gradient(180deg, #0d1020, #050610 62%)',
    scenery: {
      B: drawFacade,
      K: drawHut,
      H: drawHouse,
      T: drawTree,
      V: drawVending,
    },
    spots: {
      // 中の人は見えない。窓口の奥は暗いままで、時おり目だけが光る
      w: { kind: 'exchange', key: 'window', color: '#8ef2ff', faceless: true, sign: 'KAC' },
      // ここに立っている人。店とは関係ないことになっている
      e: { kind: 'talk', key: 'stray', color: '#ffb454' },
    },
    doors: {
      D: { to: 'hall', spawn: [7, 18], key: 'enter', color: '#ff8ad4' },
      h: { to: 'home', spawn: [6, 8], key: 'home', color: '#ffd166' },
    },
    spawn: [10, 3],
  },

  home: {
    id: 'home',
    map: HOME_MAP,
    solid: '#SBtPX',
    npcs: '',
    palette: { floor: ['#241f33', '#201b2d'], wall: '#12101c', edge: 'rgba(255,200,140,0.30)' },
    ambient: 'rgba(255,190,120,0.10)',
    background:
      'radial-gradient(120% 55% at 50% 0%, rgba(255, 170, 90, 0.22), transparent 68%),'
      + 'linear-gradient(180deg, #1a1626, #08070e 62%)',
    scenery: {
      B: drawBed,
      t: drawTable,
      P: drawPlant,
    },
    // 棚は持っている景品を載せて描くので、板だけ用意して中身は world.js に任せる
    spots: {
      S: { kind: 'collection', key: 'shelf', color: '#ffd166', draw: drawShelf, shelf: true },
    },
    doors: { X: { to: 'street', spawn: [2, 14], key: 'out', color: '#8ef2ff' } },
    spawn: [6, 8],
  },
};
